import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/ProcessStepper.css';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    n: '01',
    title: 'Understand Your Technology Landscape',
    desc: 'We align our platform with the technologies your business relies on - from frameworks to operating systems. One-time setup, under 5 minutes.',
    detail: 'We support 600+ technologies and automatically detect version ranges for intelligent CVE filtering.',
    visual: (
      <div className="step-visual step-visual--connect">
        {['Node.js v18', 'PostgreSQL v14', 'nginx v1.24', 'AWS Lambda', 'Redis v7'].map((t, i) => (
          <motion.div
            key={t}
            className="step-chip"
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.08 }}
          >
            <span className="step-chip__dot" />
            {t}
          </motion.div>
        ))}
      </div>
    ),
  },
  {
    n: '02',
    title: 'Identify Hidden Threats Before They Become Incidents',
    desc: 'Our system continuously observes global vulnerability trends and emerging threat patterns. 240,000+ CVEs exist - most don\'t affect you.',
    detail: 'Average reduction: 98.3% of CVEs are irrelevant to any given stack. We surface the rest.',
    visual: (
      <div className="step-visual step-visual--filter">
        <div className="filter-bar-group">
          {[
            { label: 'Total CVEs published', val: 100, color: '#3d6fa8' },
            { label: 'Relevant to your stack', val: 8, color: '#3b82f6' },
            { label: 'Critical priority today', val: 2, color: '#E24B4A' },
          ].map((b, i) => (
            <div key={i} className="filter-bar">
              <span className="filter-bar__label">{b.label}</span>
              <div className="filter-bar__track">
                <motion.div
                  className="filter-bar__fill"
                  style={{ background: b.color }}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${b.val}%` }}
                  transition={{ duration: 0.7, delay: i * 0.15, ease: 'easeOut' }}
                />
              </div>
              <span className="filter-bar__val font-mono">{b.val}%</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    n: '03',
    title: 'Deliver Clear, Actionable Security Insights',
    desc: 'We help you focus on what matters most with prioritized, actionable intelligence. Not all High-severity CVEs are equal - we layer exploit availability, EPSS scores, and your exposure context.',
    detail: 'CVSS alone is not enough. We combine 7 signals to give you a reliable priority score.',
    visual: (
      <div className="step-visual step-visual--prioritize">
        {[
          { id: 'CVE-2025-55182', sev: 'CRITICAL', score: 9.8, col: '#E24B4A' },
          { id: 'CVE-2025-49113', sev: 'HIGH', score: 8.2, col: '#EF9F27' },
          { id: 'CVE-2025-26633', sev: 'MEDIUM', score: 5.9, col: '#BA7517' },
        ].map((c, i) => (
          <motion.div
            key={c.id}
            className="priority-row"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <span className="priority-row__rank font-mono">#{i + 1}</span>
            <span className="priority-row__id font-mono">{c.id}</span>
            <span
              className="priority-row__badge font-mono"
              style={{ color: c.col, background: `${c.col}18`, border: `1px solid ${c.col}40` }}
            >
              {c.sev} {c.score}
            </span>
          </motion.div>
        ))}
      </div>
    ),
  },
  {
    n: '04',
    title: 'Support You With Expert Guidance',
    desc: 'Our team helps you interpret risks and strengthen your defenses with confidence. Mark CVEs as patched, snoozed, or accepted risk - with a full audit trail for compliance.',
    detail: 'Full audit trail for SOC2, ISO 27001, and PCI-DSS compliance teams. Exportable as PDF or JSON.',
    visual: (
      <div className="step-visual step-visual--track">
        <div className="track-grid">
          {[
            { label: 'Patched this week', val: 12, color: '#1D9E75' },
            { label: 'In progress', val: 5, color: '#EF9F27' },
            { label: 'Accepted risk', val: 3, color: '#4a5f8a' },
            { label: 'New this week', val: 8, color: '#E24B4A' },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="track-cell"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.09 }}
            >
              <div className="track-cell__num font-mono" style={{ color: item.color }}>{item.val}</div>
              <div className="track-cell__label">{item.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
];

const AUTOPLAY_DURATION = 5000;

export default function ProcessStepper() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const pausedRef = useRef(false);

  const nextStep = useCallback(() => {
    setActive(prev => (prev + 1) % steps.length);
    setProgress(0);
    startRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!inView) return;
    startRef.current = Date.now();

    const tick = () => {
      if (!pausedRef.current) {
        const elapsed = Date.now() - startRef.current;
        const pct = Math.min(elapsed / AUTOPLAY_DURATION, 1);
        setProgress(pct);
        if (pct >= 1) {
          nextStep();
        }
      }
      timerRef.current = requestAnimationFrame(tick);
    };

    timerRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(timerRef.current);
  }, [inView, nextStep]);

  const handleClick = (i) => {
    setActive(i);
    setProgress(0);
    startRef.current = Date.now();
  };

  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeave = () => {
    pausedRef.current = false;
    startRef.current = Date.now() - progress * AUTOPLAY_DURATION;
  };

  return (
    <section id="process" className="process section">
      <div className="container">
        <motion.div
          ref={ref}
          className="process__head"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <div className="section-eyebrow">Our Process</div>
          <h2 className="process__h2">At CVEarity, we follow a focused security intelligence workflow</h2>
          <p className="process__sub">
            To keep your organization protected without noise.
          </p>
        </motion.div>

        <div
          className="process__body"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="process__steps">
            {steps.map((s, i) => (
              <motion.button
                key={i}
                className={`process__step ${active === i ? 'process__step--on' : ''}`}
                onClick={() => handleClick(i)}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.45, delay: 0.1 + i * 0.09 }}
              >
                <span className="process__step-n font-mono">{s.n}</span>
                <div className="process__step-text">
                  <div className="process__step-title">{s.title}</div>
                  {active === i && (
                    <motion.div
                      className="process__step-desc"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {s.desc}
                    </motion.div>
                  )}
                </div>
                {active === i && (
                  <div className="process__step-progress">
                    <div
                      className="process__step-progress-fill"
                      style={{ transform: `scaleX(${progress})` }}
                    />
                  </div>
                )}
              </motion.button>
            ))}
          </div>

          <div className="process__panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                className="process__panel-inner"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
              >
                {steps[active].visual}
                <p className="process__detail">{steps[active].detail}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
